import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Hammer, Plus, Trash2, ArrowRight, Package, GitBranch, FileCode2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  ready:      "bg-teal/15 text-teal",
  analyzing:  "bg-saffron/15 text-saffron",
  empty:      "bg-ink/10 text-ink2",
  failed:     "bg-marigold/15 text-marigold",
};

export default function KailashDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/kailash/projects")
      .then(({ data }) => setProjects(data.projects || []))
      .catch(() => setProjects([]))
      .finally(() => setLoadingList(false));
  }, [user, loading, nav]);

  const remove = async (id) => {
    if (!window.confirm("Delete this project and all generated build configs?")) return;
    try {
      await api.delete(`/kailash/projects/${id}`);
      setProjects(projects.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const totalFiles = projects.reduce((s, p) => s + (p.understanding?.file_count || 0), 0);

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="kailash-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow mb-2 inline-flex items-center gap-2">
            <Hammer className="w-3 h-3" /> Kailash workbench
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold">Your projects</h1>
          <p className="text-ink2 mt-2 text-sm">Bring code. Pick Linux targets. Get real, runnable build configuration.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/apps/kailash" className="btn-ghost text-sm" data-testid="kailash-about-link">About Kailash</Link>
          <Link to="/kailash/new" className="btn-primary" data-testid="kailash-new-btn">
            <Plus className="w-4 h-4" /> New project
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="kailash-stats">
        <Stat icon={<Hammer className="w-4 h-4" />} label="Projects" value={projects.length} />
        <Stat icon={<FileCode2 className="w-4 h-4" />} label="Indexed files" value={totalFiles} />
        <Stat icon={<Package className="w-4 h-4" />} label="Build configs" value={projects.reduce((s, p) => s + (p.build_configs_count || 0), 0)} />
      </div>

      {loadingList ? (
        <div className="text-center text-ink2 py-16">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="p-12 rounded-2xl border-2 border-dashed border-border text-center" data-testid="kailash-empty">
          <Hammer className="w-10 h-10 mx-auto mb-4 text-ink2" />
          <h3 className="font-heading text-2xl font-semibold mb-2">No projects yet</h3>
          <p className="text-ink2 max-w-md mx-auto mb-6">
            Import a GitHub repo or drop a ZIP. Kailash will analyze the stack and let you generate build config for any supported Linux target.
          </p>
          <Link to="/kailash/new" className="btn-primary" data-testid="kailash-cta-create">
            <Plus className="w-4 h-4" /> Create your first project
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="kailash-project-list">
          {projects.map((p) => (
            <div key={p.id} className="p-4 rounded-2xl border border-border bg-cream hover:border-ink/40 transition-colors group" data-testid={`kailash-project-${p.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#8DA3B022", color: "#526972" }}>
                  <Hammer className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading font-semibold truncate">{p.name}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5 ${STATUS_COLORS[p.status] || STATUS_COLORS.empty}`}>{p.status}</span>
                    {p.source === "git" && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-ink2 font-mono"><GitBranch className="w-3 h-3" /> {p.git_full_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-ink2 truncate">
                    {p.understanding?.file_count ? `${p.understanding.file_count} files · ${p.understanding.primary_language || "mixed"}` : "No files indexed yet"}
                    {p.understanding?.top_modules?.length ? ` · ${p.understanding.top_modules.slice(0, 3).join(" / ")}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="btn-ghost text-xs" onClick={() => remove(p.id)} data-testid={`kailash-project-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <Link to={`/kailash/projects/${p.id}`} className="btn-primary text-xs" data-testid={`kailash-project-open-${p.id}`}>
                    Open <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="p-4 rounded-2xl bg-cream border border-border">
      <div className="text-xs text-ink2 inline-flex items-center gap-1.5 mb-1">{icon} {label}</div>
      <div className="font-heading text-2xl font-semibold">{value}</div>
    </div>
  );
}
